import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "semantic-ui-css/semantic.min.css";
import { MainContentLayout } from "./components";
import { IdleManager } from "./components/user-idel/IdleManager";
import { COMMON_PROTECT_ROUTE_PATH, IDLE_TIME, THEME, WARNING_TIME } from "./config/constants";
import { AuthContextProvider } from "./context/AuthContext";
import "./fonts.scss";
import { errorView } from "./helpers/ErrorHandler";
import { useThemeContext } from "./hooks/useThemeContext";
import "./index.css";
import "./index.scss";
import ProtectRoute from "./protectRoute";
import { MainRoutes } from "./routes";
import "./styles.scss";
import "./styles/tw-scope.css";
import { TOKEN } from "./utils/cacheStorage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      cacheTime: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onError: (error: any) => {
        if (error?.status === 401) {
          window.location.href = "/auth";
          errorView(error?.msg);
        }
      }
    }
  }
});

const AppContent: React.FC = () => {
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith("/auth");

  return (
    <>
      {!isAuthRoute && <IdleManager idleTime={IDLE_TIME} warningTime={WARNING_TIME} />}
      <MainContentLayout>
        <Routes>
          {MainRoutes.map((routesDetails, index) => (
            <Route path={routesDetails.mainPath} key={index}>
              {routesDetails.routes.map((subRoutes, index) => (
                <Route
                  key={index}
                  path={subRoutes.path}
                  element={
                    <ProtectRoute
                      protectRoutes={subRoutes.protectRoutes}
                      roleGrantAccess={subRoutes?.roleGrantAccess}
                      redirectTo={COMMON_PROTECT_ROUTE_PATH}
                    >
                      {subRoutes.route}
                    </ProtectRoute>
                  }
                />
              ))}
            </Route>
          ))}
          <Route path="*" element={<h1>Not Found</h1>} />
        </Routes>
      </MainContentLayout>
    </>
  );
};

const App: React.FC = () => {
  const { dispatch } = useThemeContext();

  useEffect(() => {
    dispatch({ type: THEME });
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === TOKEN && !event.newValue) {
        window.location.replace("/auth");
      }
    };
    window.addEventListener("storage", onStorageChange);
    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  return (
    <div className="app">
      <AuthContextProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </QueryClientProvider>
      </AuthContextProvider>
    </div>
  );
};

export default App;
